import { useMutation, useQuery } from '@tanstack/react-query';
import { apiCall } from '../../lib/api.js';
import { useAccessToken, requireToken } from '../../lib/use-access-token.js';

export interface EvlEmployeeMatch {
  id: string;
  displayName: string;
  workEmail: string | null;
  jobTitle: string | null;
}

export function useEvlEmployeeSearch(
  companyId: string | undefined,
  query: string,
  opts: { limit?: number; enabled?: boolean } = {},
) {
  const { token } = useAccessToken();
  const trimmed = query.trim();
  return useQuery<{ items: EvlEmployeeMatch[] }, Error>({
    queryKey: ['evl', 'employees', 'search', companyId, trimmed, opts.limit ?? 20, token],
    enabled: !!companyId && !!token && (opts.enabled ?? true),
    queryFn: async ({ signal }) => {
      const url = new URL(
        `/v1/companies/${companyId}/modules/evl/employees/search`,
        window.location.origin,
      );
      url.searchParams.set('q', trimmed);
      url.searchParams.set('limit', String(opts.limit ?? 20));
      return apiCall(`${url.pathname}?${url.searchParams.toString()}`, {
        token: requireToken(token),
        signal,
      });
    },
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export interface EvlIntakeBody {
  employeeId: string;
  recipient: {
    name: string;
    email: string;
    organisation?: string;
    addressLine1?: string;
    city?: string;
    state?: string;
    postal?: string;
    country?: string;
  };
  purpose: string;
  note?: string;
}

export interface EvlRunCreated {
  runId: string;
  status: string;
  hatchetWorkflowRunId: string | null;
}

export function useCreateEvlRun(companyId: string) {
  const { token } = useAccessToken();
  return useMutation<EvlRunCreated, Error, EvlIntakeBody>({
    mutationFn: async (body) =>
      apiCall(`/v1/companies/${companyId}/modules/evl/runs`, {
        method: 'POST',
        token: requireToken(token),
        body,
      }),
  });
}

export interface EvlRunStatus {
  id: string;
  status: string;
  moduleVersion: number | null;
  startedAt: string;
  completedAt: string | null;
  lastStep: string | null;
  error: string | null;
  hatchetWorkflowRunId: string | null;
  input: unknown;
}

export function useEvlRun(companyId: string | undefined, runId: string | undefined) {
  const { token } = useAccessToken();
  return useQuery<EvlRunStatus, Error>({
    queryKey: ['evl', 'run', companyId, runId, token],
    enabled: !!companyId && !!runId && !!token,
    queryFn: async ({ signal }) =>
      apiCall(`/v1/companies/${companyId}/modules/evl/runs/${runId}`, {
        token: requireToken(token),
        signal,
      }),
    refetchInterval: (q) => {
      const terminal = new Set(['completed', 'failed', 'cancelled', 'rejected']);
      const status = q.state.data?.status;
      return status && terminal.has(status) ? false : 3000;
    },
  });
}
